"use client";

import { useTranslations } from "next-intl";
import { trpc } from "@/lib/trpc-client";
import { LinkPreviewRow, PreviewMessage } from "./LinkPreviewRow";
import { useSettledValue } from "./useSettledValue";

/**
 * Who a handle on the form belongs to, under its input: the profile picture
 * and display name the platform shows, so a typo reads as a stranger's face
 * rather than as nothing at all.
 *
 * Advisory. The form never waits on it or blocks on it; the server makes its
 * own check when the application is sent. A miss is only worded as a warning
 * when the server is sure of it (see previewInstagramProfile for how that is
 * decided on Instagram); an answer it can't be sure of is shown as "no
 * preview", since telling someone their real account is missing would be
 * worse than saying nothing.
 */
export function SocialProfilePreview({
  platform,
  handle,
}: {
  platform: "tiktok" | "instagram";
  handle: string;
}) {
  const t = useTranslations("apply.fields.profilePreview");
  const typed = handle.trim();
  const settled = useSettledValue(typed);
  const query = trpc.creatorApplication.previewProfile.useQuery(
    { platform, handle: settled },
    {
      enabled: settled.length > 0,
      retry: false,
      staleTime: 10 * 60_000,
      refetchOnWindowFocus: false,
    },
  );

  if (!typed) return null;
  if (settled !== typed || query.isFetching) {
    return (
      <PreviewMessage>
        {t("looking", { handle: settled || typed })}
      </PreviewMessage>
    );
  }

  const data = query.data;
  if (!data || data.status === "unknown") {
    return <PreviewMessage>{t("unknown")}</PreviewMessage>;
  }
  if (data.status === "invalid") {
    return <PreviewMessage warn>{t("invalid")}</PreviewMessage>;
  }
  if (data.status === "not_found") {
    return (
      <PreviewMessage warn>
        {t("notFound", {
          handle: data.handle,
          platform: platform === "tiktok" ? "TikTok" : "Instagram",
        })}
      </PreviewMessage>
    );
  }
  return (
    <LinkPreviewRow
      imageUrl={data.avatarUrl}
      imageAlt={t("avatarAlt", { handle: data.handle })}
      imageShape="person"
      label={t("applyingAs")}
      value={data.name ?? `@${data.handle}`}
    />
  );
}
